import {useState, useEffect, useRef} from 'react';
import config from '../../../oktaConfig';
import {useOktaAuth} from '@okta/okta-react';
import SwitchResponseData from '../interfaces/response/SwitchResponseData';
import FetchResponseError from 'interfaces/FetchResponseError';
import PortHistoryResponseData from '../interfaces/response/PortHistoryResponseData';
import useUserLogs from 'hooks/useUserLogs';

function useSwitchConnectivity(siteId: string, switchNames: string[]) {
  const {authState, oktaAuth} = useOktaAuth();
  const [switchDataIsLoaded, setSwitchDataIsLoaded] = useState<boolean>(false);
  const [portHistoryDataIsLoaded, setPortHistoryDataIsLoaded] = useState<boolean>(false);
  const [switchData, setSwitchData] = useState<SwitchResponseData | null>(null);
  const [portHistoryData, setPortHistoryData] = useState<PortHistoryResponseData[] | null>(null);
  const [error, setError] = useState<FetchResponseError | null>(null);
  const {dispatch: dispatchUserLog} = useUserLogs();

  // When specific switches are selected we scope the request with `switchnames=`.
  // Otherwise (e.g. retail stores which don't use the switch selection UI) we
  // fall back to scoping by `siteId=` so the backend returns all switches for
  // the site. The `switchnames=` param is intentionally omitted in that case.
  const hasSwitchNames = Array.isArray(switchNames) && switchNames.length > 0;
  const switchQueryParam = hasSwitchNames
    ? `?switchnames=${switchNames.join(',')}`
    : `?siteId=${siteId}`;

  const prevState = useRef<{
    switchNames: undefined | string[];
    siteId: undefined | string;
  }>({
    switchNames: undefined,
    siteId: undefined,
  });

  function reloadData() {
    prevState.current.switchNames = undefined;
    prevState.current.siteId = undefined;
    setPortHistoryDataIsLoaded(false);
    setSwitchDataIsLoaded(false);
    setPortHistoryData(null);
    setSwitchData(null);
    setError(null);
  }

  useEffect(() => {
    // if auth state changes but siteId and switchNames have not, return early to avoid unnecessary re-renders
    if (prevState.current.switchNames === switchNames && prevState.current.siteId === siteId) {
      return;
    }

    prevState.current.switchNames = switchNames;
    prevState.current.siteId = siteId;

    async function fetchPortHistory(accessToken: string, controller: AbortController) {
      let portHistoryFetchResult = await fetch(
        config.resourceServer.insightsApiUrlSwitchPortHistory + switchQueryParam,
        {
          mode: 'cors',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        }
      );

      if (!portHistoryFetchResult.ok) return Promise.reject(portHistoryFetchResult);

      setPortHistoryData(await portHistoryFetchResult.json());
      setPortHistoryDataIsLoaded(true);
    }

    async function fetchSwitchData(accessToken: string | undefined, controller: AbortController) {
      fetch(config.resourceServer.insightsApiUrlSwitchData + switchQueryParam, {
        mode: 'cors',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      })
        .then(response => {
          if (!response.ok) {
            return Promise.reject(response);
          }
          return response.json();
        })
        .then(data => {
          setSwitchData(data);
          dispatchUserLog('Switch Connectivity');
        })
        .then(() => {
          setSwitchDataIsLoaded(true);
        })
        .catch(err => {
          console.error(err);
          console.log('error', err);
          if (err.name === 'AbortError') {
            const errorMsg: FetchResponseError = {
              ok: false,
              status: 'userAbortedRequest',
              statusText: 'DOMException: The user aborted a request.',
              type: '',
              url: config.resourceServer.insightsApiUrlSwitchData + switchQueryParam,
            };
            setError(errorMsg);
          } else {
            setError(err);
          }

          setSwitchDataIsLoaded(false);
        });
    }

    setSwitchDataIsLoaded(false);
    setSwitchData(null);
    setError(null);
    const controller = new AbortController();
    if (authState && authState.isAuthenticated && (hasSwitchNames || siteId)) {
      const accessToken = oktaAuth.getAccessToken();
      const controller = new AbortController();
      setError(null);

      fetchPortHistory(accessToken!!, controller);
      fetchSwitchData(accessToken, controller);
    } else {
      setSwitchData(null);
      setPortHistoryData(null);
      setSwitchDataIsLoaded(false);
      setPortHistoryDataIsLoaded(false);
      setError(null);
    }

    return () => {
      controller.abort();
    };
  }, [
    switchNames,
    siteId,
    authState,
    oktaAuth,
    prevState.current.switchNames,
    prevState.current.siteId,
  ]);

  let allDataLoaded = switchDataIsLoaded && portHistoryDataIsLoaded;

  return {
    allDataLoaded,
    switchData,
    portHistoryData,
    reloadData,
    error,
  };
}

export default useSwitchConnectivity;
